import {Breadcrumb} from "antd";
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {AppContextContext, AttributeDefinitionServiceContext, VulSourceItemServiceContext} from "../../Contexts";
import VulSourceItem from "../../domain/VulSourceItem";
import {TableUiConfig, useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import * as styles from "../vulsourceitem/VulSourceItemList.module.css";
import * as globalStyles from "../App.module.css";
import VulSourceItemFilter, {createDefaultVulSourceItemFilterValues, MIN_ATTRIBUTE_VALUE_SEARCH_LENGTH} from "./VulSourceItemFilter";
import _ from "lodash";
import {FormPersister} from "../../sal-ui/FormPersister";
import AttributeDefinition from "../../domain/AttributeDefinition";
import QueryOptions from "../../sal-ui/QueryOptions";
import VulSourceItemListTable from "./VulSourceItemListTable";
import {observer} from "mobx-react-lite";
import {Permission} from "../../domain/auth/Permission";
import {isValuelessAttributeOperator} from "../../utils/RqbOperatorUtils";

const defaultVisibleColumns = ["id", "description", "sourcePublished"];

const defaultTableUiConfig = {immediateMode: false, visibleColumns: defaultVisibleColumns, showAiAnalytics: true};

const WITHDRAWN_ATTRIBUTE_NAME = "withdrawn";

type AttributeFilterValue = {
    name?: string;
    operator?: string;
    value?: any;
};

type CompleteAttributeFilterValue = AttributeFilterValue & {
    name: string;
    operator: string;
};

const VulSourceItemList = observer(() => {
    const persistentIdent = 'VulSourceItemList';
    const appContext = useContext(AppContextContext);
    const vulSourceItemService = useContext(VulSourceItemServiceContext);
    const attributeDefinitionService = useContext(AttributeDefinitionServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const filterFormPersister = new FormPersister(persistentIdent);
    const tableHandler = useTableHandler("sourcePublished desc", {
        reloadFunction: reload,
        initialPageSize: 50,
        persistentIdent
    });
    const [filterValues, setFilterValues] = useState<any>();
    const readOnly = useRef(false);
    const [vulSourceItems, setVulSourceItems] = useState<PagedResult<VulSourceItem>>();
    const [tableUiConfig, setTableUiConfig] = useState<TableUiConfig>(defaultTableUiConfig);
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>();

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        attributeDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setAttributeDefinitions(value.data));

        const searchParams = new URLSearchParams(location.search);

        readOnly.current = searchParams.has('$readOnly');

        const loadedValues = (searchParams.has("filter"))
            ? filterFormPersister.loadValues(FormPersister.decodeUrlState(searchParams.get("filter")!))
            : filterFormPersister.loadValues();

        const values = _.isEmpty(loadedValues) ? createDefaultVulSourceItemFilterValues() : loadedValues;

        setFilterValues(values);

        if (searchParams.has("filter")) {
            navigate(location.pathname, {replace: true});
        }

        setTableUiConfig(tableHandler.loadUiConfig() || defaultTableUiConfig);
    }, [])

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    const debouncedOnFinishFilter = useCallback(_.debounce(onFinishFilter, 500), []);

    const title = "Vulnerability items";

    return (
        <DocumentTitle title={title}>
            {!appContext.presentationMode && <Breadcrumb
                className={globalStyles["common__breadcrumb"]}
                items={[
                    {title: appContext.config?.appName},
                    {title}
                ]}
            />}

            <h1>{title}</h1>

            <div style={{display: (appContext.presentationMode) ? 'none' : 'inherit'}}>
                <VulSourceItemFilter
                    className={styles.filter}
                    onChange={values => {
                        setFilterValues(values);
                    }}
                    values={filterValues}
                    persistentStateIdent={persistentIdent}
                    showViews={appContext.user.hasOneOfPermission([Permission.VIEW__READ_ORG, Permission.VIEW__READ_SEAL])}
                    showAiAttributeFilters={appContext.user.hasOneOfPermission([Permission.AI_ATTRIBUTE__READ_ORG, Permission.AI_ATTRIBUTE__READ_SEAL])}
                    immediateMode={tableUiConfig.immediateMode}
                    attributeDefinitions={attributeDefinitions}
                />
            </div>

            <VulSourceItemListTable
                persistentIdent={persistentIdent}
                items={vulSourceItems}
                tableHandler={tableHandler}
                tableUiConfig={tableUiConfig}
                setTableUiConfig={setTableUiConfig}
            />
        </DocumentTitle>
    )

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: []
        }

        values.sources && values.sources.length > 0 && rqbQuery.rules.push({
            field: "source.id",
            operator: "in",
            value: values.sources
        });

        const attributeValue = normalizeAttributeValueSearch(values.attributeValue);

        attributeValue && rqbQuery.rules.push({
            field: "attributes",
            operator: "jsonIcontains",
            value: attributeValue
        });

        values.cveId && rqbQuery.rules.push({
            field: "attributes",
            operator: "jsonEq",
            value: ["cveId", values.cveId]
        });

        pushDateRangeRule(rqbQuery, "created", values.createdFrom, values.createdTo);
        pushDateRangeRule(rqbQuery, "sourcePublished", values.sourcePublishedFrom, values.sourcePublishedTo);
        pushDateRangeRule(rqbQuery, "lastModified", values.lastModifiedFrom, values.lastModifiedTo);
        pushDateRangeRule(rqbQuery, "dataChanged", values.dataChangedFrom, values.dataChangedTo, {useBetween: true});
        pushDateRangeRule(rqbQuery, "attributesChanged", values.attributesChangedFrom, values.attributesChangedTo, {useBetween: true});

        if (values.vulView) {
            const hasValues = Array.isArray(values.vulView) ? values.vulView.length > 0 : true;

            if (hasValues) {
                rqbQuery.rules.push({
                    field: "vulView.id",
                    operator: "in",
                    value: values.vulView
                });
            }
        }

        if (values.aiAttributeFilter && values.aiAttributeFilter.length > 0) {
            values.aiAttributeFilter.forEach(filter => {
                const aiAttributeIdentifier = filter.aiAttributeId;

                if (aiAttributeIdentifier && filter.operator && filter.value !== undefined && filter.value !== null && filter.value !== '') {
                    rqbQuery.rules.push({
                        field: `aiAttributes.${aiAttributeIdentifier}`,
                        operator: filter.operator,
                        value: filter.value
                    });
                }
            });
        }

        values.attributes && values.attributes.length > 0 && values.attributes.forEach((filter: AttributeFilterValue | undefined) => {
            if (!isCompleteAttributeFilter(filter)) {
                return;
            }

            rqbQuery.rules.push(createAttributeFilterRule(filter));
        });

        return rqbQuery;
    }

    function createAttributeFilterRule(filter: CompleteAttributeFilterValue) {
        if (filter.name === WITHDRAWN_ATTRIBUTE_NAME && filter.operator === "jsonEq" && filter.value === false) {
            return {
                combinator: "or",
                rules: [
                    {
                        field: "attributes",
                        operator: "jsonMissing",
                        value: WITHDRAWN_ATTRIBUTE_NAME
                    },
                    {
                        field: "attributes",
                        operator: "jsonEq",
                        value: [WITHDRAWN_ATTRIBUTE_NAME, false]
                    }
                ]
            };
        }

        if (isValuelessAttributeOperator(filter.operator)) {
            return {
                field: "attributes",
                operator: filter.operator,
                value: filter.name
            };
        }

        return {
            field: "attributes",
            operator: filter.operator,
            value: [filter.name, filter.value]
        };
    }

    function isCompleteAttributeFilter(filter?: AttributeFilterValue): filter is CompleteAttributeFilterValue {
        if (!filter?.name || !filter.operator) {
            return false;
        }

        if (isValuelessAttributeOperator(filter.operator)) {
            return true;
        }

        if (filter.value === undefined || filter.value === null) {
            return false;
        }

        if (typeof filter.value === "string" && filter.value.trim().length === 0) {
            return false;
        }

        return !(typeof filter.value === "number" && Number.isNaN(filter.value));
    }

    function normalizeAttributeValueSearch(value: any) {
        const textValue = String(value ?? "").trim();

        return textValue.length >= MIN_ATTRIBUTE_VALUE_SEARCH_LENGTH ? textValue : undefined;
    }

    function pushDateRangeRule(rqbQuery: any, field: string, from: any, to: any, options: {useBetween?: boolean} = {}) {
        if (options.useBetween && from && to) {
            rqbQuery.rules.push({
                field,
                operator: "between",
                value: [
                    from.utc().format(),
                    to.utc().format()
                ]
            });

            return;
        }

        from && rqbQuery.rules.push({
            field,
            operator: "ge",
            value: from.utc().format()
        });

        to && rqbQuery.rules.push({
            field,
            operator: "le",
            value: to.utc().format()
        });
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createRqbQuery(values);

        if (!readOnly.current) {
            filterFormPersister.saveState(values);
        }

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function reload() {
        return vulSourceItemService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setVulSourceItems(value);
        });
    }

});

export default VulSourceItemList;
